import { Button, Card, Modal, State, Toast } from '@seiue/ui'
import { findIndex } from '@seiue/util'
import { Affix } from 'antd'
import { useAtom } from 'jotai'
import React, { useState } from 'react'
import styled from 'styled-components'

import { paperApi } from 'packages/sdks-next/growp'

import { TaskLayout } from '@/components/TaskLayout'
import { QuotePaper } from '@/uikit/QuotePaper'

import { focusedBlockAtom } from '../../components/BlockList/atoms'

import { EditContent } from './Content'
import { ScoreSettingCard } from './ScoreSetting/ScoreSettingCard'
import { EditPaperContext } from './context'
import { insertQuoteBlocks, quoteBlocksFormat } from './utils'

/**
 * 编辑试卷弹窗
 *
 * @param param - 参数
 * @param param.paperId - 试卷ID
 * @param param.onSuccess - 弹窗确定|保存按钮成功回调
 * @param param.onClose - 弹窗关闭回调
 * @param param.controlVisible - 弹窗是否是手动可控
 * @param param.visible - 手动可控时的参数
 * @param param.showQuote - 显示引用
 * @param param.title - 弹窗标题
 * @param param.isRand - 是否是抽题模式【只能创建和引用选择题】
 * @param param.limitLevel - 限制级别（L）
 *   - 【级别范围：禁止的操作】
 *   - L=0：任意修改（默认）
 *   - L>0：题目：答案题型修改
 *   - L>1：题目：分数修改、新增题目
 *   - L>2：题目：题干解析修改，编辑保存
 *   - L>3：题目：禁止删除
 *   - 0<L<=2：题目：删除时弹窗提醒
 * @returns React.ReactNode
 */
export const EditPaper: React.FC<{
  paperId: number
  onSuccess: () => void
  onClose: () => void
  controlVisible?: boolean
  visible?: boolean
  showQuote?: boolean
  title?: string
  isRand?: boolean
  limitLevel?: number
}> = ({
  paperId,
  onSuccess,
  onClose,
  controlVisible,
  visible,
  showQuote,
  title,
  isRand,
  limitLevel,
}) => {
  const [focusedBlock] = useAtom(focusedBlockAtom)
  const [quotoVisible, setQuoteVisible] = useState<boolean>(false)
  const [container, setContainer] = useState<HTMLDivElement | null>(null)

  const { data: paper, loading } = paperApi.useLoadPaper({
    id: paperId,
    query: {
      expand: ['questionCount'],
    },
  })

  const { data: blocks } = paperApi.useQueryBlocks({
    paperId: paperId,
    query: {
      sort: 'weight',
      expand: ['questions'],
      paginated: 0,
    },
  })

  const { api: apiBatchSortBlocks } = paperApi.useBatchSortBlocks({
    reload: [paperApi.queryBlocks, paperApi.loadPaper],
  })
  return (
    <>
      <QuotePaper
        visible={quotoVisible}
        onClose={() => setQuoteVisible(false)}
        onOk={async (v) => {
          const { data } = await paperApi.queryBlocks(v, {
            sort: 'weight',
            expand: ['questions'],
            paginated: 0,
          })
          if (!data) {
            Toast.warning('引用试卷数据为空')
            return
          }
          // 批量创建blocks之前对引用的内容进行处理
          const newData = quoteBlocksFormat(data, paperId, isRand)
          // 批量创建blocks
          const { data: quoteBlocks } = await paperApi.batchCreateBlock(newData)
          // 插入引用的blocks并重新按照weight排序
          let insertIndex
          if (focusedBlock.id === -1) {
            insertIndex = (blocks?.length || 0) + 1
          } else {
            insertIndex =
              findIndex(blocks, {
                id: focusedBlock.id,
              }) + 1
          }
          const payload = insertQuoteBlocks(
            blocks || [],
            quoteBlocks,
            insertIndex
          )
          await apiBatchSortBlocks({
            paperId,
            payload,
          })
          setQuoteVisible(false)
        }}
      />
      <Modal
        fullScreen={true}
        visible={controlVisible ? visible : true}
        header={null}
      >
        {loading ? (
          <State.Loading />
        ) : !paper ? null : (
          <EditPaperContext.Provider
            value={{ paper, isRand: !!isRand, limitLevel: limitLevel || 0 }}
          >
            <TaskLayout
              title={title || paper?.name || '-'}
              HeaderRight={
                showQuote ? (
                  <Button
                    disabled={(limitLevel || 0) > 1}
                    onClick={() => {
                      setQuoteVisible(true)
                    }}
                  >
                    引用试卷
                  </Button>
                ) : null
              }
              onClose={onClose}
              okText="保存"
              onOk={onSuccess}
            >
              <Content
                ref={setContainer}
                id="vbjknbkad"
              >
                <Card
                  style={{
                    width: 960,
                    borderRadius: 8,
                    minHeight: '50vh',
                  }}
                  contentStyle={{ padding: '24px 48px 24px 16px' }}
                >
                  <EditContent blocks={blocks} />
                </Card>
                <div
                  style={{
                    position: 'absolute',
                    top: 84,
                    left: 'calc(50% + 370px)',
                  }}
                >
                  <ScoreSettingCard
                    paper={paper}
                    blocks={blocks || []}
                  />
                </div>
              </Content>
            </TaskLayout>
          </EditPaperContext.Provider>
        )}
      </Modal>
    </>
  )
}

const Content = styled.div`
  width: 1220px;
  display: flex;
  justify-content: space-between;
`
